import {
  applyAllowances,
  applyBreakPeriods,
  applyCustomFields,
  breakPeriodsContainer,
  hhmm,
  minutesOf,
  namedInput,
  notify,
  readAllowances,
  readBreakPeriods,
  readCustomFields,
  readScalar,
  writeScalar,
} from "./timesheet_voice_form"

// Writes a voice_extract result onto the timesheet form and reads the form back
// as a correction draft (timesheet_voice_controller). On the FIRST pass every
// suggested key is applied; on later passes only the server's `changed_keys`
// diff, so a correction overwrites exactly what the speaker changed.
//
// Rows: on a Mehrbaustellenbuchung form the suggestions carry `site_allocations`,
// on a Mehrfacheintrag form `additional_entries` (block 2 onwards; block 1 is the
// form's own fields). Rows are added and removed through the row controllers
// (multi-site-booking, multi-entry), never by hand, so their renumbering, the
// rest-row auto-fill, block chaining and Pause-Blöcke stay in charge.
//
// Every value that finds no field on the form is collected in `missing`.
export class VoiceFormWriter {
  // +formElement+: the form; +application+: the Stimulus application, to reach
  // the row, select and nested-form controllers.
  constructor(formElement, application) {
    this.formElement = formElement
    this.application = application
  }

  // --- Applying suggestions ---

  applySuggestions(payload) {
    const suggestions = payload.suggestions || {}
    const changedKeys = payload.changed_keys
    const pick = (bucket) => {
      const values = suggestions[bucket] || {}
      if (!changedKeys) return values
      const allowed = changedKeys[bucket] || []
      return Object.fromEntries(Object.entries(values).filter(([key]) => allowed.includes(key)))
    }

    this.applied = 0
    this.missing = []
    const form = this.form()
    this.applyCore(form, "configurable_time_sheet", pick("configurable_time_sheet"))
    this.applyRowValues(form, "custom_fields", "allowances", {
      custom_fields: pick("custom_fields"),
      allowances: pick("allowances"),
    })
    if ("site_allocations" in suggestions) {
      this.applySiteAllocations(suggestions.site_allocations, changedKeys?.site_allocations)
    }
    if ("additional_entries" in suggestions) {
      this.applyAdditionalEntries(suggestions.additional_entries, changedKeys?.additional_entries)
    }
    return { applied: this.applied, missing: this.missing }
  }

  // Core values of the form (prefix configurable_time_sheet) or of one row.
  applyCore(scope, prefix, values) {
    // reported_date deliberately gets NO change event: seasonal-default-times
    // listens on it and would asynchronously overwrite the just-applied
    // time_from/time_to/time_break with the template defaults.
    if ("reported_date" in values) {
      const input = this.findFormInput("reported_date")
      if (input) {
        input.value = values.reported_date
        input.dispatchEvent(new Event("input", { bubbles: true }))
        this.applied += 1
      } else {
        this.missing.push("reported_date")
      }
    }

    ;["time_from", "time_to", "time_break", "hourly_wage", "notes", "general_notes"].forEach(
      (key) => {
        if (!(key in values)) return
        if (writeScalar(scope, `${prefix}[${key}]`, values[key])) {
          this.applied += 1
        } else if (prefix === "configurable_time_sheet" && this.findFormInput(key)) {
          const input = this.findFormInput(key)
          input.value = values[key]
          notify(input)
          this.applied += 1
        } else {
          this.missing.push(key)
        }
      },
    )

    if ("break_periods" in values) {
      if (applyBreakPeriods(scope, values.break_periods || [], this.nestedFormIn(scope))) {
        this.applied += 1
      } else {
        this.missing.push("break_periods")
      }
    }

    if ("site_id" in values) {
      const siteSelect = namedInput(scope, `${prefix}[site_id]`)
      if (siteSelect) {
        this.setSelectValue(siteSelect, String(values.site_id ?? ""))
        this.applied += 1
      } else {
        this.missing.push("site_id")
      }
    }
    if ("task_ids" in values && Array.isArray(values.task_ids)) {
      const name = `${prefix}[task_ids][]`
      if (scope.querySelector(`select[name="${name}"]`)) {
        // site-tasks repopulates the dropdown asynchronously after the site
        // change above — poll briefly until the options exist.
        this.applyTaskIdsWhenReady(scope, name, values.task_ids, 15)
        this.applied += 1
      } else {
        this.missing.push("task_ids")
      }
    }
  }

  applyRowValues(scope, customPrefix, allowancePrefix, row) {
    const customValues = row.custom_fields || {}
    const allowanceValues = row.allowances || {}
    const missingFields = applyCustomFields(scope, customPrefix, customValues, (select, value) =>
      this.setSelectValue(select, value),
    )
    const missingAllowances = applyAllowances(scope, allowancePrefix, allowanceValues)
    this.applied += Object.keys(customValues).length - missingFields.length
    this.applied += Object.keys(allowanceValues).length - missingAllowances.length
    this.missing.push(...missingFields, ...missingAllowances)
  }

  applyTaskIdsWhenReady(scope, name, taskIds, attemptsLeft) {
    const select = scope.querySelector(`select[name="${name}"]`)
    if (!select) return
    const optionValues = Array.from(select.options).map((option) => parseInt(option.value, 10))
    const allPresent = taskIds.every((id) => optionValues.includes(id))
    if (!allPresent && attemptsLeft > 0) {
      setTimeout(() => this.applyTaskIdsWhenReady(scope, name, taskIds, attemptsLeft - 1), 200)
      return
    }
    this.setSelectValue(
      select,
      taskIds.map((id) => String(id)),
    )
  }

  // --- Mehrbaustellenbuchung ---

  applySiteAllocations(rows, changes) {
    const booking = this.rowController("multi-site-booking")
    if (!booking) {
      if (rows.length) this.missing.push("site_allocations")
      return
    }

    const plan = this.rowPlan(rows, changes, booking.allocationTargets.length)
    plan.remove
      .map((index) => booking.allocationTargets[index])
      .filter(Boolean)
      .forEach((row) => {
        const button = row.querySelector("[data-action='click->multi-site-booking#removeSite']")
        if (button) booking.removeSite({ target: button })
      })
    while (booking.allocationTargets.length < plan.size) booking.addSite()

    plan.apply.forEach(({ index, keys, customKeys, allowanceKeys }) => {
      const element = booking.allocationTargets[index]
      const row = rows[index]
      if (!element || !row) return
      const prefix = this.rowPrefix(element, "site_allocations")
      const core = this.only(row, keys, ["site_id", "task_ids"])
      this.applyCore(element, prefix, core)
      if (!keys || keys.includes("hours")) this.applyAllocationHours(booking, element, prefix, row)
      this.applyRowValues(element, `${prefix}[custom_fields]`, `${prefix}[allowances]`, {
        custom_fields: this.only(row.custom_fields, customKeys),
        allowances: this.only(row.allowances, allowanceKeys),
      })
    })
    booking.updateTotals()
  }

  // Spoken hours make the row manual (input -> hoursEdited). "Den Rest" (no hours)
  // makes it the row that follows the day's remainder.
  applyAllocationHours(booking, element, prefix, row) {
    const input = namedInput(element, `${prefix}[hours]`)
    if (!input) return
    if (row.hours) {
      input.value = row.hours
      input.dispatchEvent(new Event("input", { bubbles: true }))
    } else {
      input.value = ""
      booking.followRemainder(element)
    }
    this.applied += 1
  }

  // --- Mehrfacheintrag ---

  applyAdditionalEntries(rows, changes) {
    const entries = this.rowController("multi-entry")
    if (!entries) {
      if (rows.length) this.missing.push("additional_entries")
      return
    }

    // entryTargets[0] is block 1 (the form's own fields); rows start at block 2.
    // A first pass rebuilds the further blocks, so each gets its Pause-Block
    // (or folded pause) exactly as spoken.
    if (!changes) entries.entryTargets.slice(1).forEach((entry) => this.removeEntry(entries, entry))
    const plan = this.rowPlan(rows, changes, entries.entryTargets.length - 1)
    plan.remove
      .map((index) => entries.entryTargets[index + 1])
      .filter(Boolean)
      .forEach((entry) => this.removeEntry(entries, entry))

    plan.apply.forEach(({ index, added, keys, customKeys, allowanceKeys }) => {
      const row = rows[index]
      if (!row) return
      let element = entries.entryTargets[index + 1]
      if (added || !element) element = this.addEntryBlock(entries, row.break_before)
      if (!element) {
        this.missing.push("additional_entries")
        return
      }
      const prefix = entries.entryPrefix(element)
      const core = this.only(row, keys, [
        "time_from",
        "time_to",
        "time_break",
        "break_periods",
        "site_id",
        "task_ids",
      ])
      // A chained block starts where the one before it ends; its Beginn is not ours to set.
      if (entries.chainValue) delete core.time_from
      this.applyCore(element, prefix, core)
      // A new block got its pause from addEntryBlock; a corrected one gets it here.
      if (!added && keys?.includes("break_before")) this.correctBreakBefore(entries, element, row)
      this.applyRowValues(
        element,
        this.childPrefix(prefix, "custom_fields"),
        this.childPrefix(prefix, "allowances"),
        {
          custom_fields: this.only(row.custom_fields, customKeys),
          allowances: this.only(row.allowances, allowanceKeys),
        },
      )
    })
    entries.chainBlocks()
  }

  removeEntry(entries, entry) {
    const button = entry.querySelector("[data-action='click->multi-entry#remove']")
    if (button) entries.remove({ target: button })
  }

  // A pause between two chained blocks: a Pause-Block where the form offers one,
  // otherwise written onto the block before — its window runs over the pause and
  // the pause becomes its break, which is what saving a Pause-Block produces.
  addEntryBlock(entries, breakBefore) {
    const count = entries.entryTargets.length
    if (count >= entries.maxEntriesValue) return null

    const previous = entries.entryTargets[count - 1]
    if (breakBefore && entries.chainValue && entries.hasBreakButtonTarget) {
      entries.addWithBreak()
      const breakRows = entries.containerTarget.querySelectorAll("[data-multi-entry-break-row]")
      const breakTo = breakRows[breakRows.length - 1]?.querySelector("[data-break-to]")
      if (breakTo) {
        breakTo.value = breakBefore.break_to
        breakTo.dispatchEvent(new Event("change", { bubbles: true }))
      }
    } else {
      if (breakBefore && entries.chainValue) this.foldBreak(entries, previous, breakBefore)
      entries.add()
    }
    return entries.entryTargets[count] || null
  }

  // The pause before an existing block, as corrected: moved, added or dropped.
  correctBreakBefore(entries, element, row) {
    if (!entries.chainValue) return
    const breakBefore = row.break_before
    const before = element.previousElementSibling
    let breakRow = before?.hasAttribute("data-multi-entry-break-row") ? before : null

    if (!breakBefore) {
      if (breakRow) entries.removeBreak({ target: breakRow })
    } else if (!entries.hasBreakButtonTarget) {
      const index = entries.entryTargets.indexOf(element)
      this.foldBreak(entries, entries.entryTargets[index - 1], breakBefore)
    } else {
      if (!breakRow) {
        breakRow = entries.buildBreakRow()
        element.before(breakRow)
      }
      // The block before may have moved its end in this same correction: anchor
      // the pause at the spoken start so the chain keeps its spoken length.
      const from = minutesOf(breakBefore.break_from)
      if (from !== null) breakRow.dataset.from = String(from)
      const breakTo = breakRow.querySelector("[data-break-to]")
      if (breakTo) {
        breakTo.value = breakBefore.break_to
        breakTo.dispatchEvent(new Event("change", { bubbles: true }))
      }
    }
    this.applied += 1
  }

  foldBreak(entries, block, breakBefore) {
    const from = minutesOf(breakBefore.break_from)
    const to = minutesOf(breakBefore.break_to)
    if (from === null || to === null || to <= from) return

    const prefix = entries.entryPrefix(block)
    writeScalar(block, `${prefix}[time_to]`, breakBefore.break_to)
    if (entries.periodsModeValue && breakPeriodsContainer(block)) {
      const periods = readBreakPeriods(block) || []
      applyBreakPeriods(block, [...periods, breakBefore], this.nestedFormIn(block))
    } else {
      const current = minutesOf(readScalar(block, `${prefix}[time_break]`)) || 0
      writeScalar(block, `${prefix}[time_break]`, hhmm(current + to - from))
    }
  }

  // --- Row plumbing ---

  // What to do with the rows: on a first pass every suggested row is applied in
  // full and surplus rows go; on a correction only the reported row changes.
  rowPlan(rows, changes, existing) {
    if (!changes) {
      const remove = []
      for (let index = existing - 1; index >= rows.length; index--) remove.push(index)
      return {
        size: rows.length,
        remove,
        apply: rows.map((_, index) => ({ index, added: index >= existing })),
      }
    }

    const remove = changes
      .filter((change) => change.status === "removed")
      .map((change) => change.index)
      .sort((a, b) => b - a)
    const apply = changes
      .filter((change) => change.status !== "removed")
      .sort((a, b) => a.index - b.index)
      .map((change) =>
        change.status === "added"
          ? { index: change.index, added: true }
          : {
              index: change.index,
              keys: change.keys || [],
              customKeys: change.custom_fields || [],
              allowanceKeys: change.allowances || [],
            },
      )
    return {
      size: Math.max(existing - remove.length, ...apply.map((step) => step.index + 1)),
      remove,
      apply,
    }
  }

  // Picks +keys+ out of +values+ (all of them when keys is undefined), limited to +allowed+.
  only(values, keys, allowed) {
    const source = values || {}
    return Object.fromEntries(
      Object.entries(source).filter(
        ([key]) => (!keys || keys.includes(key)) && (!allowed || allowed.includes(key)),
      ),
    )
  }

  rowController(identifier) {
    const element = this.form()?.querySelector(`[data-controller~="${identifier}"]`)
    return element
      ? this.application.getControllerForElementAndIdentifier(element, identifier)
      : null
  }

  // A block cloned in this same pass (addEntryBlock) has no connected nested-form
  // controller yet — Stimulus connects on its next mutation tick — so its von/bis
  // rows are written straight into the markup, as nested-form#add_association would.
  nestedFormIn(scope) {
    const container = breakPeriodsContainer(scope)
    if (!container) return null
    return (
      this.application.getControllerForElementAndIdentifier(container, "nested-form") ||
      unconnectedNestedForm(container)
    )
  }

  // The name prefix of a row: site_allocations[N] / additional_entries[N] (N is the
  // row's stamp, not its position).
  rowPrefix(element, bucket) {
    const pattern = new RegExp(`^${bucket}\\[\\d+\\]`)
    const input = Array.from(element.querySelectorAll("[name]")).find((el) => pattern.test(el.name))
    return input ? input.name.match(pattern)[0] : `${bucket}[0]`
  }

  // Block 1's per-entry values sit in the top-level buckets.
  childPrefix(entryPrefix, bucket) {
    return entryPrefix === "configurable_time_sheet" ? bucket : `${entryPrefix}[${bucket}]`
  }

  // Selects in this app are wrapped by SlimSelect (select_controller), whose
  // rendered UI does not follow programmatic .value writes — go through the
  // controller's setSelected so the visible state updates too, then bubble a
  // change for the dependent controllers (site-tasks, dynamic-form, …).
  setSelectValue(select, valueOrValues) {
    const controller = this.application.getControllerForElementAndIdentifier(select, "select")
    if (controller && typeof controller.setSelected === "function") {
      controller.setSelected(valueOrValues)
    } else if (Array.isArray(valueOrValues)) {
      Array.from(select.options).forEach((option) => {
        option.selected = valueOrValues.includes(option.value)
      })
    } else {
      select.value = valueOrValues
    }
    notify(select)
  }

  // --- Rückfragen ---

  // Where a question's field sits: the form itself (bucket "day", block 1 of a
  // Mehrfacheintrag form) or one row. null when the row is gone.
  locate(target) {
    if (target.bucket === "day") {
      return {
        scope: this.form(),
        prefix: "configurable_time_sheet",
        customPrefix: "custom_fields",
      }
    }
    if (target.bucket === "site_allocations") {
      const element = this.rowController("multi-site-booking")?.allocationTargets[target.row]
      if (!element) return null
      const prefix = this.rowPrefix(element, "site_allocations")
      return { scope: element, prefix, customPrefix: `${prefix}[custom_fields]` }
    }
    if (target.bucket === "additional_entries") {
      const entries = this.rowController("multi-entry")
      const element = entries?.entryTargets[target.row + 1]
      if (!element) return null
      const prefix = entries.entryPrefix(element)
      return { scope: element, prefix, customPrefix: this.childPrefix(prefix, "custom_fields") }
    }
    return null
  }

  // The input name of a question's field.
  fieldNameFor(target) {
    const place = this.locate(target)
    if (!place) return null
    if (target.scope === "custom_fields") return `${place.customPrefix}[${target.key}]`
    return target.key === "task_ids"
      ? `${place.prefix}[task_ids][]`
      : `${place.prefix}[${target.key}]`
  }

  // Writes a tapped option into the question's field. A list that takes several
  // values (tasks, multi-select) gets the option added to what it holds.
  // +replacing+: the value an ambiguous question already put into the field (its
  // best match) — on a multi-select the tapped option takes its place instead of
  // joining it.
  applyAnswer(target, value, { replacing = null } = {}) {
    this.applied = 0
    this.missing = []
    const place = this.locate(target)
    if (!place) return 0
    if (target.scope === "custom_fields") {
      const name = `${place.customPrefix}[${target.key}]`
      const multi = place.scope.querySelector(`select[name="${name}[]"]`)
      const answer = multi ? this.answerSelection(multi, value, replacing) : value
      this.applyRowValues(place.scope, place.customPrefix, `${place.prefix}[allowances]`, {
        custom_fields: { [target.key]: answer },
        allowances: {},
      })
    } else if (target.key === "task_ids") {
      const select = place.scope.querySelector(`select[name="${place.prefix}[task_ids][]"]`)
      const ids = select ? this.answerSelection(select, value, replacing) : [String(value)]
      this.applyCore(place.scope, place.prefix, { task_ids: ids })
    } else {
      this.applyCore(place.scope, place.prefix, { [target.key]: value })
    }
    return this.applied
  }

  // A multi-select's values after a tapped answer: the selection without the
  // replaced best match, plus the answer, each once.
  answerSelection(select, value, replacing) {
    const kept = this.selectedValues(select).filter(
      (selected) => replacing == null || selected !== String(replacing),
    )
    return [...new Set([...kept, String(value)])]
  }

  selectedValues(select) {
    return Array.from(select.selectedOptions)
      .map((option) => option.value)
      .filter((value) => value !== "")
  }

  // --- Draft serialization (correction passes) ---

  serializeDraft() {
    const form = this.form()
    if (!form) return null

    const draft = {
      configurable_time_sheet: this.readCore(form, "configurable_time_sheet", [
        "reported_date",
        "time_from",
        "time_to",
        "time_break",
        "site_id",
        "hourly_wage",
        "notes",
        "general_notes",
        // The Buchungsziel: it answers a required Baustelle (voice_questions).
        "fleet_id",
      ]),
      custom_fields: readCustomFields(form, "custom_fields"),
      allowances: readAllowances(form, "allowances"),
    }
    const reportedDate = this.findFormInput("reported_date")
    if (reportedDate?.value) draft.configurable_time_sheet.reported_date = reportedDate.value

    const booking = this.rowController("multi-site-booking")
    if (booking) {
      draft.site_allocations = booking.allocationTargets.map((element) => {
        const prefix = this.rowPrefix(element, "site_allocations")
        return {
          ...this.readCore(element, prefix, ["site_id", "hours"]),
          custom_fields: readCustomFields(element, `${prefix}[custom_fields]`),
          allowances: readAllowances(element, `${prefix}[allowances]`),
        }
      })
    }

    const entries = this.rowController("multi-entry")
    if (entries) {
      const first = entries.entryTargets[0]
      const firstPeriods = first ? readBreakPeriods(first) : null
      if (firstPeriods) draft.configurable_time_sheet.break_periods = firstPeriods
      draft.additional_entries = entries.entryTargets
        .slice(1)
        .map((element) => this.readEntry(entries, element))
    } else {
      const periods = readBreakPeriods(form)
      if (periods) draft.configurable_time_sheet.break_periods = periods
    }
    return draft
  }

  readCore(scope, prefix, keys) {
    const core = {}
    keys.forEach((key) => {
      const value = readScalar(scope, `${prefix}[${key}]`)
      if (value !== null) core[key] = value
    })
    const taskSelect = scope.querySelector(`select[name="${prefix}[task_ids][]"]`)
    if (taskSelect) {
      // The blank placeholder option counts as selected and would send [null].
      const ids = Array.from(taskSelect.selectedOptions)
        .filter((option) => option.value !== "")
        .map((option) => parseInt(option.value, 10))
      if (ids.length) core.task_ids = ids
    }
    return core
  }

  readEntry(entries, element) {
    const prefix = entries.entryPrefix(element)
    const entry = {
      ...this.readCore(element, prefix, ["time_from", "time_to", "time_break", "site_id"]),
      custom_fields: readCustomFields(element, `${prefix}[custom_fields]`),
      allowances: readAllowances(element, `${prefix}[allowances]`),
    }
    const periods = readBreakPeriods(element)
    if (periods) entry.break_periods = periods
    const breakRow = element.previousElementSibling
    if (
      breakRow?.hasAttribute("data-multi-entry-break-row") &&
      breakRow.dataset.from &&
      breakRow.dataset.to
    ) {
      entry.break_before = {
        break_from: hhmm(Number(breakRow.dataset.from)),
        break_to: hhmm(Number(breakRow.dataset.to)),
      }
    }
    return entry
  }

  selectedEmployeeId() {
    const form = this.form()
    const select = form?.querySelector('select[name*="[employee_ids]"]')
    if (select && select.selectedOptions.length === 1) return select.selectedOptions[0].value
    return null
  }

  form() {
    return this.formElement
  }

  findFormInput(fieldName) {
    const form = this.form()
    if (!form) return null
    return (
      namedInput(form, `configurable_time_sheet[${fieldName}]`) ||
      form.querySelector(`#configurable_time_sheet_${fieldName}`)
    )
  }
}

// The add/remove half of nested_form_controller.js for a nested-form element whose
// controller has not connected yet — same markup, same stamp, same _destroy flag.
function unconnectedNestedForm(container) {
  const template = container.querySelector("template[data-nested-form-target='template']")
  const links = container.querySelector("[data-nested-form-target='links']")
  if (!template || !links) return null

  const placeholder = container.getAttribute("data-nested-form-placeholder-value") || "NEW_RECORD"
  let stamp = Date.now()
  return {
    add_association() {
      stamp += 1
      links.insertAdjacentHTML(
        "beforebegin",
        template.innerHTML.replaceAll(placeholder, String(stamp)),
      )
    },
    remove_association({ target }) {
      const wrapper = target.closest(".nested-fields")
      if (!wrapper) return
      if (wrapper.dataset.newRecord === "true") {
        wrapper.remove()
      } else {
        const destroy = wrapper.querySelector("input[name*='_destroy']")
        if (destroy) destroy.value = 1
        wrapper.style.display = "none"
      }
      container.dispatchEvent(new Event("change", { bubbles: false }))
    },
  }
}
